import Vue from 'vue'

import users from '@/api/users'
import pin from '@/api/pin'
import * as types from '../mutation-types'
import auth from '@/libs/auth'
import location from '@/libs/location'

const state = {
  user: null,
  verified: false,
  haveToken: false,
  loggedIn: false,
  hasPassword: false,
  hasPhone: false,
  promptToSetPassword: false,
  showSignupSurvey: false,
  showSignupCategories: false,
  profiles: {},
  skills: {},
  settings: {},
  awards: {},
  settingsPromise: null,
  firebaseToken: null,
  cloudMessagingToken: null,
  limitedUser: null,
  profileUpdateCount: 0,
  referralCode: null,
  userSelectedCategories: [],
  userFilterTags: [],
  userFilterTagsRemember: false,
  hasYSCInfo: false,
  hasAJASRole: false,
  hasPartnerInfo: false,
  resourceButtonClicked: false,
  isRegisteredForWebinar: false,
  isSignupComplete: false,
  handles: [],
  signupNames: [],
  updateUserTypeError: null,
  teacherGroup: {},
  teacherClasses: [],
  studentList: [],
  classCode: null,
  studentQuestionId: 0,
  studentName: null
}

const getters = {
  currentUser: state => state.user !== null ? state.user : {},
  loggedIn: state => state.user !== null && state.verified && state.haveToken,
  hasCurrentUser: state => state.user !== null,
  needsVerification: state => state.user !== null && !state.verified,
  promptToSetPassword: state => state.promptToSetPassword,
  showSignupSurvey: state => state.showSignupSurvey,
  showSignupCategories: state => state.showSignupCategories,
  firebaseToken: state => state.firebaseToken,
  userHasPassword: state => state.hasPassword,
  userHasPhone: state => state.hasPhone,
  referralCode: state => state.referralCode,
  userCategories: state => state.userSelectedCategories,
  userFilterTags: state => state.userFilterTags,
  userFilterTagsRemember: state => state.userFilterTagsRemember,
  userAwards: state => state.awards !== null ? state.awards : {},
  resourceButtonClicked: state => state.resourceButtonClicked,
  isRegistered: state => state.isRegisteredForWebinar,
  isSignupComplete: state => state.isSignupComplete,
  ajasRole: state => state.hasAJASRole,
  handles: state => state.handles,
  hasPassword: state => state.hasPassword,
  signupNames: state => state.signupNames,
  updateUserTypeError: state => state.updateUserTypeError,
  teacherGroup: (state) => {
    return state.teacherGroup
  },
  teacherClasses: state => state.teacherClasses,
  studentList: state => state.studentList,
  classCode: state => state.classCode,
  studentQuestionId: state => state.studentQuestionId,
  studentName: state => state.studentName
}

const responseTokens = (response) => {
  return {
    accessToken: response.headers.authorization,
    refreshToken: response.headers['refresh-token']
  }
}

const parseToken = function (token, length) {
  try {
    token = atob(token).split('|')
    if (!token || token.length !== length) {
      return false
    }
  } catch (error) {
    return false
  }

  return token
}

const postGetUserDispatch = function (dispatch, user) {
  dispatch('saveLocation', user.userName)
  dispatch('savePushNotificationToken', state.cloudMessagingToken)
  dispatch('logUserHubspotID')
  dispatch('getUserSettings', user.userName)
  dispatch('getUserAwardPermissions', user.userName)
  // requiresYSCInfo removed from user
  // if (user.requiresYSCInfo && !state.hasYSCInfo) {
  //   dispatch('getYscUserInfo')
  // }
  if (user.signUpSource.includes('?signup=true&redirect=fablabsthankyou') && !state.hasPartnerInfo) {
    dispatch('getPartnerUserInfo')
  }
}

/* eslint-disable prefer-promise-reject-errors */
const actions = {
  preAuth ({ commit }, { username, email, disableModals }) {
    return new Promise((resolve, reject) => {
      users.check(username, email).then((response) => {
        const user = response.data.result.user
        const hasPassword = response.data.result.hasPassword
        const isVerified = response.data.result.isVerified
        const hasPhone = response.data.result.hasPhone
        const isOAuth = response.data.result.isOAuth
        commit(types.SET_CURRENT_USER, user)
        commit(types.SET_USER_VERIFIED, false)
        commit(types.SET_HAS_PASSWORD, hasPassword)
        commit(types.SET_HAS_PHONE, hasPhone)
        resolve({ user, hasPassword, isVerified, hasPhone, isOAuth })
      }, (error) => {
        reject(error)
      })
    })
  },
  login ({ commit, dispatch }, { email, username, password, doubleOptIn, redirect, loginSource, disableModals }) {
    return new Promise((resolve, reject) => {
      users.login(email, username, password, doubleOptIn, redirect, loginSource).then((response) => {
        commit(types.RESET_ON_LOGOUT)
        const user = response.data.result.user
        commit(types.SET_CURRENT_USER, user)
        commit(types.SET_USER_VERIFIED, response.data.result.verified)
        commit(types.SET_HAS_PASSWORD, response.data.result.hasPassword)
        commit(types.SET_REFERRAL_CODE)
        if (response.data.result.verified) {
          postGetUserDispatch(dispatch, user)
          resolve(responseTokens(response))
        } else {
          resolve(user)
        }
      }, (error) => {
        reject(error)
      })
    })
  },
  makeLogin ({ commit, dispatch }, result) {
    return new Promise((resolve, reject) => {
      commit(types.RESET_ON_LOGOUT)
      const user = result.user
      commit(types.SET_CURRENT_USER, user)
      commit(types.SET_USER_VERIFIED, result.verified)
      commit(types.SET_HAS_PASSWORD, result.hasPassword)
      resolve({ accessToken: result.accessToken, refreshToken: result.refreshToken, created: result.created, userVerified: result.lastVerified, signupSource: result.signupSource })
    })
  },
  engcomLogin ({ commit, dispatch }, { hubspotTrackingID, utmSource, referralCode }) {
    return new Promise((resolve, reject) => {
      users.createAccountFromEngcom(hubspotTrackingID, utmSource, referralCode).then((response) => {
        const user = response.data.result.user
        if (!user) {
          return reject()
        }

        commit(types.SET_CURRENT_USER, user)
        commit(types.SET_USER_VERIFIED, response.data.result.verified)
        if (response.data.result.verified) {
          postGetUserDispatch(dispatch, user)
        }

        auth.clearUTMSource()
        auth.clearReferralCode()
      }, (error) => {
        reject(error)
      })
    })
  },
  getLocation: function () {
    return new Promise((resolve) => {
      location.get().then((response) => {
        resolve(response.data)
      }, () => {
        resolve(false)
      })
    })
  },
  saveLocation ({ dispatch }, userName) {
    dispatch('getLocation').then((location) => {
      users.saveLocationData(userName, location)
    }).catch(() => {
    })
  },
  verify ({ commit, dispatch }, code) {
    return new Promise((resolve, reject) => {
      users.verify(state.user.userName, code).then((response) => {
        const user = response.data.result.user

        commit(types.SET_CURRENT_USER, user)
        commit(types.SET_USER_VERIFIED, true)
        commit(types.SET_REFERRAL_CODE, response.data.result.referralCode)
        commit(types.SET_HAS_PASSWORD, response.data.result.hasPassword)

        resolve(responseTokens(response))
        postGetUserDispatch(dispatch, user)
      }, (error) => {
        reject(error)
      })
    })
  },
  verifyToken ({ commit, dispatch }, { token, username, disableModals }) {
    return new Promise((resolve, reject) => {
      let userName = username
      let verificationCode = token
      if (!userName) {
        token = (token).split('|') // Need to figure out how to encode this code in the backend
        if (!token) {
          return reject([this.$t('error.invalid.token')])
        }
        userName = token[0]
        verificationCode = token[1]
      }

      users.verify(userName, verificationCode).then((response) => {
        const user = response.data.result.user
        const tokens = responseTokens(response)
        const hasPassword = response.data.result.hasPassword

        commit(types.SET_CURRENT_USER, user)
        commit(types.SET_USER_VERIFIED, true)
        commit(types.SET_HAS_PASSWORD, response.data.result.hasPassword)

        resolve({ tokens, hasPassword })
        postGetUserDispatch(dispatch, user)
      }, (error) => {
        reject(error)
      })
    })
  },
  checkLastVerified ({ commit, dispatch }, token) {
    return new Promise((resolve, reject) => {
      token = parseToken(token, 3)
      if (!token) {
        return reject([this.$t('error.invalid.token')])
      }

      users.checkLastVerified(token[0]).then((response) => {
        const isVerified = response.data.result.isVerified
        resolve(isVerified)
      }, (error) => {
        reject(error)
      })
    })
  },
  acceptInvite ({ commit, dispatch }, { token, username, password, receiveEmails }) {
    return new Promise((resolve, reject) => {
      token = parseToken(token, 3)
      if (!token) {
        return reject([this.$t('error.invalid.token')])
      }
      users.acceptInvite(token[0], token[1], token[2], username, password, receiveEmails).then((response) => {
        const projectID = token[1]
        const user = response.data.result.user
        let tokens = null
        if (user) {
          tokens = responseTokens(response)
          commit(types.SET_CURRENT_USER, user)
          commit(types.SET_USER_VERIFIED, true)
          postGetUserDispatch(dispatch, user)
        }
        resolve({ projectID, tokens })
      }, (error) => {
        reject(error)
      })
    })
  },
  refresh ({ commit, dispatch }, data) {
    return new Promise((resolve, reject) => {
      users.refresh(data.username, data.token).then((response) => {
        const user = response.data.result.user
        commit(types.SET_CURRENT_USER, user)
        commit(types.SET_USER_VERIFIED, true)

        resolve(responseTokens(response))
      }, (error) => {
        reject(error)
      })
    })
  },
  signup ({ commit, dispatch }, data) {
    return new Promise((resolve, reject) => {
      users.signup(data).then((response) => {
        const user = response.data.result.user
        const group = user.group
        if (user.signUpSource !== 'TeacherCode') {
          commit(types.SET_TEACHER_GROUP, group)
        }
        commit(types.SET_CURRENT_USER, user)
        commit(types.SET_USER_VERIFIED, false)
        commit(types.SET_HAS_PASSWORD, true)
        resolve(user)

        auth.storeTokens({ accessToken: response.headers.authorization, limited: true })
        auth.clearUTMSource()
        auth.clearReferralCode()

        resolve({ user: user, tokens: responseTokens(response) })
        dispatch('saveLocation', user.userName)

        dispatch('trackUserSignup')
      }, (error) => {
        reject(error)
      })
    })
  },
  limitedLogin ({ commit, dispatch }, token) {
    return new Promise((resolve, reject) => {
      token = parseToken(token, 3)
      if (!token) {
        return reject([this.$t('error.invalid.token')])
      }

      users.limitedLogin(token[0], token[1], token[2]).then((response) => {
        const user = response.data.result.user
        commit(types.SET_LIMITED_USER, user)
        resolve({ token: response.headers.authorization, user })
      }, (error) => {
        reject(error)
      })
    })
  },
  current ({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      users.current().then((response) => {
        const user = response.data.result.user
        commit(types.SET_CURRENT_USER, user)
        commit(types.SET_USER_VERIFIED, true)
        commit(types.SET_REFERRAL_CODE, response.data.result.referralCode)
        resolve()
        postGetUserDispatch(dispatch, user)
      }, (error) => {
        reject(error)
      })
    })
  },
  logout ({ commit }) {
    return new Promise((resolve, reject) => {
      users.logout().then(() => {
        commit(types.RESET_ON_LOGOUT)
        resolve()
      }, error => reject(error))
    })
  },
  createPassword ({ commit }, { username, password }) {
    return new Promise((resolve, reject) => {
      users.createPassword(username, password).then(() => {
        resolve()
      }, error => reject(error))
    })
  },
  setPassword ({ commit }, { username, password, oldPassword }) {
    return new Promise((resolve, reject) => {
      users.changePassword(username, password, oldPassword).then(() => {
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  setEmail ({ commit }, { username, email }) {
    return new Promise((resolve, reject) => {
      users.changeEmail(username, email).then(() => {
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  hideSignupSurvey ({ commit }) {
    commit(types.SET_SHOW_SIGNUP_SURVEY, false)
  },
  getUser ({ commit }, username) {
    return new Promise((resolve, reject) => {
      users.get(username).then((response) => {
        resolve(response.data.result.user)
      }, (error) => {
        reject(error)
      })
    })
  },
  getUsers ({ commit }, { limit, offset, query }) {
    return new Promise((resolve, reject) => {
      users.getAll(limit, offset, query).then((response) => {
        const users = response.data.result.users
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        resolve({ users, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getAllLikeName ({ commit }, { limit, offset, query }) {
    return new Promise((resolve, reject) => {
      users.getAllLikeName(limit, offset, query).then((response) => {
        const users = response.data.result.users
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        resolve({ users, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getAllLikeSchoolName ({ commit }, { limit, offset, page, query }) {
    return new Promise((resolve, reject) => {
      users.getAllLikeSchoolName(limit, offset, page, query).then((response) => {
        const schools = response.data.result.schools
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        resolve({ schools, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getLikedProjects ({ commit }, username) {
    return new Promise((resolve, reject) => {
      users.getLikedProjects(username).then((response) => {
        resolve(response.data.result.projects)
      }, (error) => {
        reject(error)
      })
    })
  },
  getResourceRecommendations ({ commit }, { userID, resourceID, limit, offset }) {
    return new Promise((resolve, reject) => {
      users.getResourceRecommendations(userID, resourceID, limit, offset).then((response) => {
        const resources = response.data.result.resources
        const total = response.data.result.total
        const hasMore = response.data.result.hasMore
        resolve({ resources, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getQuestionsForUserOrResource ({ commit }, { userID, resourceID }) {
    return new Promise((resolve, reject) => {
      users.getQuestionsForUserOrResource(userID, resourceID).then((response) => {
        const questions = response.data.result.questions
        resolve({ questions })
      }, (error) => {
        reject(error)
      })
    })
  },
  getInternalQuestions ({ commit }, { userID }) {
    return new Promise((resolve, reject) => {
      users.getInternalQuestions(userID).then((response) => {
        const questions = response.data.result.questions
        const answers = response.data.result.answers
        resolve({ questions, answers })
      }, (error) => {
        reject(error)
      })
    })
  },
  getPartnerQuestions ({ commit }, { userID, limit, offset }) {
    return new Promise((resolve, reject) => {
      users.getPartnerQuestions(userID, limit, offset).then((response) => {
        const questions = response.data.result.questions
        const total = response.data.result.total
        const hasMore = response.data.result.hasMore
        resolve({ questions, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getPartnerQuestionsByGrouping ({ commit }, { userID, grouping, filterByType }) {
    return new Promise((resolve, reject) => {
      users.getPartnerQuestionsByGrouping(userID, grouping, filterByType).then((response) => {
        const questions = response.data.result.questions
        const hasMore = response.data.result.hasMore
        resolve({ questions, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getLikedComments ({ commit }, username) {
    return new Promise((resolve, reject) => {
      users.getLikedComments(username).then((response) => {
        resolve(response.data.result.comments)
      }, (error) => {
        reject(error)
      })
    })
  },
  getProfile ({ commit, state }, username) {
    return new Promise((resolve, reject) => {
      if (state.profiles[username]) {
        return resolve(state.profiles[username])
      }
      users.getProfile(username).then((response) => {
        const profile = response.data.result.profile
        commit(types.ADD_PROFILE, { username, profile })
        resolve(state.profiles[username])
      }, (error) => {
        reject(error)
      })
    })
  },
  getProfileCounts ({ commit, state }, { username }) {
    return new Promise((resolve) => {
      const data = { limit: 0, offset: 0, filter: 'project', count: true }
      users.getProjects(username, data).then((response) => {
        commit(types.ADD_PROFILE_COUNT, { username, key: 'projects', total: response.data.result.total })
        data.filter = 'story'
        users.getProjects(username, data).then((response) => {
          commit(types.ADD_PROFILE_COUNT, { username, key: 'stories', total: response.data.result.total })
        })
      })

      const filter = ''
      users.getAllContributions(username, 0, 0, '', filter, true).then((response) => {
        commit(types.ADD_PROFILE_COUNT, { username, key: 'contributions', total: response.data.result.total })
      })

      users.getFollowing(username, 0, 0, true).then((response) => {
        commit(types.ADD_PROFILE_COUNT, { username, key: 'following', total: response.data.result.total })
      })

      users.getFollowers(username, 0, 0, true).then((response) => {
        commit(types.ADD_PROFILE_COUNT, { username, key: 'followers', total: response.data.result.total })
      })

      pin.getPins(username, 0, 0, true).then((response) => {
        commit(types.ADD_PROFILE_COUNT, { username, key: 'pins', total: response.data.result.total })
      })
    })
  },
  checkForUserConnection ({ commit }, { username, otherUsername }) {
    return new Promise((resolve, reject) => {
      users.isFollowing(username, otherUsername).then((response) => {
        resolve(response.data.result.following)
      }, (error) => {
        reject(error)
      })
    })
  },
  getFollowing ({ commit }, { username, limit, offset, data }) {
    return new Promise((resolve, reject) => {
      users.getFollowing(username, limit, offset, data).then((response) => {
        const users = response.data.result.users
        const total = response.data.result.total
        const hasMore = response.data.result.hasMore
        resolve({ users, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getFollowers ({ commit }, { username, limit, offset, data }) {
    return new Promise((resolve, reject) => {
      users.getFollowers(username, limit, offset, data).then((response) => {
        const users = response.data.result.users
        const total = response.data.result.total
        const hasMore = response.data.result.hasMore
        resolve({ users, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getNotifications ({ commit }, { username, limit, offset, data }) {
    return new Promise((resolve, reject) => {
      users.getNotifications(username, limit, offset, data).then((response) => {
        const notifications = response.data.result.notifications
        const total = response.data.result.total
        const hasMore = response.data.result.hasMore
        resolve({ notifications, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  connectToUser ({ commit }, { username, otherUsername }) {
    return new Promise((resolve, reject) => {
      users.follow(username, otherUsername).then((response) => {
        resolve(response)
      }, (error) => {
        reject(error)
      })
    })
  },
  disconnectFromUser ({ commit }, { username, otherUsername }) {
    return new Promise((resolve, reject) => {
      users.unfollow(username, otherUsername).then((response) => {
        resolve(response)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateProfile ({ commit, state }, { username, profile }) {
    return new Promise((resolve, reject) => {
      users.updateProfile(username, profile).then((response) => {
        const profile = response.data.result.profile
        commit(types.ADD_PROFILE, { username, profile })
        resolve(state.profiles[username])
      }, (error) => {
        reject(error)
      })
    })
  },
  updateProfileOccupation ({ commit, state }, { username, profile }) {
    return new Promise((resolve, reject) => {
      users.updateProfileOccupation(username, profile).then((response) => {
        resolve(response)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateSomeProfileInfo ({ commit, state }, { username, profile }) {
    return new Promise((resolve, reject) => {
      users.updateSomeProfileInfo(username, profile).then((response) => {
        resolve(response)
      }, (error) => {
        reject(error)
      })
    })
  },
  saveYscUserInfo ({ commit, state }, { username, profile }) {
    return new Promise((resolve, reject) => {
      users.saveYscUserInfo(username, profile).then((response) => {
        const profile = response.data.result.profile
        commit(types.ADD_PROFILE, { username, profile })
        resolve(state.profiles[username])
      }, (error) => {
        reject(error)
      })
    })
  },
  getYscUserInfo ({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      users.getYscUserInfo().then((response) => {
        if (response.data.result && response.data.result.info) {
          commit(types.HAS_YSC_INFO, true)
          resolve(response.data.result.info)
        } else {
          dispatch('showSignupSurvey')
        }
      }, (error) => {
        reject(error)
      })
    })
  },
  getAjasProjectTags ({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      users.getAjasProjectTags().then((response) => {
        if (response.data.result && response.data.result.info) {
          resolve(response.data.result.info)
        }
      }, (error) => {
        reject(error)
      })
    })
  },
  getAjasUserRole ({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      users.getAjasUserRole().then((response) => {
        if (response.data.result && response.data.result.role) {
          commit(types.HAS_AJAS_ROLE, true)
          resolve(response.data.result.info)
        } else {
          dispatch('showSignupSurvey')
        }
      }, (error) => {
        reject(error)
      })
    })
  },
  getPartnerUserInfo ({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      users.getPartnerUserInfo().then((response) => {
        const hasInfo = response.data.result?.info
        if (hasInfo) {
          commit(types.HAS_PARTNER_INFO, true)
          resolve(hasInfo)
        } else {
          Vue.prototype.$isHeise && dispatch('showSignupSurvey', { type: 'fablabs' })
        }
      }, (error) => {
        reject(error)
      })
    })
  },
  addPartnerUserInfo ({ commit, state }, { username, partnerUserInfo }) {
    return new Promise((resolve, reject) => {
      switch (process.env.APP_NAME) {
        case 'Girls-and-steam':
          users.SaveGASUserInfo(username, partnerUserInfo).then((response) => {
            resolve(response)
          }, (error) => {
            reject(error)
          })
          break
        default:
          users.SaveIsefUserInfo(username, partnerUserInfo).then((response) => {
            resolve(response)
          }, (error) => {
            reject(error)
          })
          break
      }
    })
  },
  updateCredentials ({ commit }, { username, data }) {
    return new Promise((resolve, reject) => {
      users.update(username, data).then((response) => {
        const user = response.data.result.user
        commit(types.SET_CURRENT_USER, user)
        if (username !== user.userName) {
          commit(types.MOVE_PROFILE, { from: username, to: user.userName })
        }
        resolve(user)
      }, (error) => {
        reject(error)
      })
    })
  },
  getContributions ({ commit }, { username, limit, offset, data, filter }) {
    return new Promise((resolve, reject) => {
      users.getAllContributions(username, limit, offset, data, filter).then((response) => {
        const contributions = response.data.result.contributions
        const total = response.data.result.total
        const hasMore = response.data.result.hasMore
        resolve({ contributions, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getContributionComments ({ commit }, { username, limit, offset, data }) {
    return new Promise((resolve, reject) => {
      users.getComments(username, limit, offset, data).then((response) => {
        const comments = response.data.result.comments
        const total = response.data.result.total
        const hasMore = response.data.result.hasMore
        resolve({ comments, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getContributionWhiteboards ({ commit }, { username, limit, offset, data }) {
    return new Promise((resolve, reject) => {
      users.getWhiteboards(username, limit, offset, data).then((response) => {
        const whiteboards = response.data.result.whiteboards
        const total = response.data.result.total
        const hasMore = response.data.result.hasMore
        resolve({ whiteboards, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  findUser ({ commit }, lookup) {
    return new Promise((resolve, reject) => {
      reject(new Error('Deprecated'))
    })
  },
  setSkills ({ commit }, { username, skills }) {
    return new Promise((resolve, reject) => {
      users.setSkills(username, { tags: skills }).then((response) => {
        const skills = response.data.result.skills
        if (state.profiles[username]) {
          commit(types.SET_SKILLS, { username, skills })
          resolve(skills)
        } // else not setting it because we should never get here and it will be set on the next call to getProfile
      }, (error) => {
        reject(error)
      })
    })
  },
  setInterests ({ commit }, { username, interests }) {
    return new Promise((resolve, reject) => {
      users.setInterests(username, { tags: interests }).then((response) => {
        const interests = response.data.result.interests
        if (state.profiles[username]) {
          commit(types.SET_INTERESTS, { username, interests })
        } // else not setting it because we should never get here and it will be set on the next call to getProfile
        resolve(interests)
      }, (error) => {
        reject(error)
      })
    })
  },
  getUserCategories ({ commit }, username) {
    return new Promise((resolve, reject) => {
      users.getCategories(username).then((response) => {
        resolve(response.data.result.categories)
      }, (error) => {
        reject(error)
      })
    })
  },
  getFilterOptions ({ commit }) {
    return new Promise((resolve, reject) => {
      users.getFilters().then((response) => {
        resolve(response.data.result.filters)
      }, (error) => {
        reject(error)
      })
    })
  },
  setUserFilter ({ commit }, { username, tags, remember }) {
    if (!username) {
      commit(types.SET_USER_FILTER_TAGS, { tags })
      return
    }
    return new Promise((resolve, reject) => {
      users.setFilter(username, { tags, remember }).then((response) => {
        commit(types.SET_USER_FILTER_TAGS, { tags })
        commit(types.SET_USER_FILTER_TAGS_REMEMBER, { remember })
        resolve(tags)
      }, (error) => {
        reject(error)
      })
    })
  },
  getFilterTags ({ commit }, { username }) {
    return new Promise((resolve, reject) => {
      users.getFilterTags(username).then((response) => {
        const tagIDs = response.data.result.tags.tagIds
        const remember = response.data.result.tags.Remember
        const tags = [...tagIDs]

        if (remember) {
          commit(types.SET_USER_FILTER_TAGS, { tags })
        }

        const dict = { tagIDs: tagIDs, remember: remember }

        commit(types.SET_USER_FILTER_TAGS_REMEMBER, { remember })
        resolve(dict)
      }, (error) => {
        reject(error)
      })
    })
  },
  setUserCategories ({ commit }, { username, categories }) {
    return new Promise((resolve, reject) => {
      users.setCategories(username, { categories }).then((response) => {
        const userCategories = response.data.result.categories
        commit(types.SET_USER_CATEGORIES, { userCategories })
        resolve(userCategories)
      }, (error) => {
        reject(error)
      })
    })
  },
  getUserSettings ({ commit, state }, username) {
    // re-use existing settings call if possible
    if (state.settingsPromise) {
      return new Promise((resolve, reject) => {
        state.settingsPromise.then((settings) => {
          resolve(settings)
        }, (error) => {
          reject(error)
        })
      })
    }

    if (!state.haveToken) {
      return
    }

    const promise = new Promise((resolve, reject) => {
      users.getSettings(username).then((response) => {
        const settings = response.data.result.settings
        commit(types.SET_USER_SETTINGS, settings)
        commit(types.SET_USER_SETTINGS_PROMISE, null)
        resolve(settings)
      }, (error) => {
        commit(types.SET_USER_SETTINGS_PROMISE, null)
        reject(error)
      })
    })

    commit(types.SET_USER_SETTINGS_PROMISE, promise)

    return promise
  },
  updateUserSettings ({ commit, state }, { username, settings }) {
    return new Promise((resolve, reject) => {
      users.updateSettings(username, settings).then((response) => {
        const settings = response.data.result.settings
        commit(types.SET_USER_SETTINGS, settings)
        resolve(settings)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateUserType ({ commit, state }, { username, userType }) {
    return new Promise((resolve, reject) => {
      users.updateUserType(username, userType).then((response) => {
        commit(types.SET_CURRENT_USER, response.data.result.user)
        commit(types.UPDATE_USER_TYPE_SUCCESS)
        resolve()
      }, (error) => {
        commit(types.UPDATE_USER_TYPE_ERROR, error)
        reject(error)
      })
    })
  },
  getUserSetting ({ state, dispatch }, key) {
    return new Promise((resolve) => {
      if (!state.user) {
        return resolve(null)
      }

      if (Object.keys(state.settings).length > 0) {
        return resolve(state.settings[key])
      }

      dispatch('getUserSettings', state.user.userName).then((settings) => {
        resolve(settings[key])
      }, () => {
        resolve(null)
      })
    })
  },
  haveToken ({ commit, dispatch }, have) {
    commit(types.SET_HAVE_TOKEN, have)
    if (state.user) {
      dispatch('saveLocation', state.user.userName)
      dispatch('savePushNotificationToken', state.cloudMessagingToken)
      dispatch('logUserHubspotID')

      if (state.user.signUpSource.includes('?signup=true&redirect=fablabsthankyou') && !state.hasPartnerInfo) {
        dispatch('getPartnerUserInfo')
      }
    }
  },
  getFirebaseToken ({ commit }, reset) {
    return new Promise((resolve) => {
      if (!state.user) {
        return resolve(null)
      }

      if (state.firebaseToken && reset !== true) {
        return resolve(state.firebaseToken)
      }

      users.getFirebaseToken().then((response) => {
        const token = response.data.result.token
        commit(types.SET_FIREBASE_TOKEN, token)
        resolve(token)
      }, () => {
        resolve(null)
      })
    })
  },
  clearFirebaseToken ({ commit }) {
    return new Promise((resolve) => {
      commit(types.SET_FIREBASE_TOKEN, null)
      resolve()
    })
  },
  firebaseLogin ({ commit, dispatch }, data) {
    return new Promise((resolve, reject) => {
      users.firebaseLogin(data).then((response) => {
        const user = response.data.result.user
        commit(types.SET_CURRENT_USER, user)
        commit(types.SET_USER_VERIFIED, response.data.result.verified)
        if (!data.disableModals) {
          commit(types.SET_SHOW_SIGNUP_SURVEY, response.data.result.newUser && !data.skipSignupSurvey)
        }

        resolve({
          user,
          tokens: responseTokens(response),
          newUser: response.data.result.newUser,
          hasPassword: response.data.result.hasPassword
        })

        postGetUserDispatch(dispatch, user)

        auth.clearUTMSource()
        auth.clearReferralCode()
      }, (error) => {
        reject(error)
      })
    })
  },
  savePushNotificationToken ({ commit, state }, token) {
    if (!token) {
      return
    }

    if (state.user) {
      users.savePushNotificationToken(state.user.userName, { token })
      commit(types.SET_CLOUD_MESSAGING_TOKEN, null)
    } else {
      commit(types.SET_CLOUD_MESSAGING_TOKEN, token)
    }
  },
  socketMessage ({ commit, state }, message) {
    const payload = message.payload || {}

    if (payload.type === 'user' && payload.action === 'logout' && payload.UUID === state.user.UUID) {
      auth.logout()
    }
  },
  onlineCount ({ commit, state }, data) {
    return new Promise((resolve, reject) => {
      users.onlineCount().then((response) => {
        resolve(response.data.result.count)
      }, (error) => {
        reject(error)
      })
    })
  },
  logUserHubspotID ({ state }, data) {
    let uuid = data ? data.uuid : null
    let hubspotID = data ? data.hubspotID : null

    if (!uuid) {
      if (state.user) {
        uuid = state.user.uuid
      } else if (state.limitedUser) {
        uuid = state.limitedUser.uuid
      }
    }

    if (!hubspotID) {
      hubspotID = Vue.cookie.get('hubspotutk')
    }

    if (uuid && hubspotID) {
      users.logHubspotID({ uuid, hubspotID })
    }
  },
  tagAndAddToGroup ({ commit }, { username, data }) {
    return new Promise((resolve, reject) => {
      users.tagAndAddToGroup(username, data).then(() => {
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  autoFollowProject ({ commit }, { username, data }) {
    if (data.autoFollow > 0) {
      commit(types.SUBSCRIBE_PROJECT, { id: data.autoFollow })
      commit(types.SUBSCRIBED_TO_PROJECT, { id: data.autoFollow, username, subscribed: true })
    }
  },
  getUserAwardPermissions ({ commit }, username) {
    return new Promise((resolve, reject) => {
      users.getUserAwardPermissions(username).then((response) => {
        const awards = response.data.result.awards
        commit(types.SET_USER_AWARDS, awards)
        resolve(awards)
      }, (error) => {
        reject(error)
      })
    })
  },
  getProjectCounts ({ commit }, { username, data }) {
    const count = {}
    const params = Object.assign(data)
    return new Promise((resolve) => {
      params.filter = 'viewall'
      users.getProjects(username, params).then((response) => {
        count.all = response.data.result.total

        params.filter = 'private'
        users.getProjects(username, params).then((response) => {
          count.private = response.data.result.total

          params.filter = 'public'
          users.getProjects(username, params).then((response) => {
            count.public = response.data.result.total
            params.filter = 'anonymous'
            users.getProjects(username, params).then((response) => {
              count.anonymous = response.data.result.total
              resolve(count)
            })
          })
        })
      })
    })
  },
  saveUserResourceAnswers ({ commit }, data) {
    return new Promise((resolve, reject) => {
      users.saveUserResourceAnswers(data).then((response) => {
        const user = response.data.result.user
        commit(types.SET_CURRENT_USER, user)
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  saveUserResourceDownload ({ commit }, { resourceID, utmCampaign, utmSource, utmMedium }) {
    return new Promise((resolve, reject) => {
      users.saveUserResourceDownload(resourceID, { utmCampaign, utmSource, utmMedium }).then(() => {
        commit(types.HAS_REGISTERED, true)
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  resourceButtonClicked ({ commit }, isClicked) {
    commit(types.HAS_CLICKED_RESOURCE_BUTTON, isClicked)
  },
  isRegistered ({ commit }, registered) {
    commit(types.HAS_REGISTERED, registered)
  },
  checkWebinarRegistration ({ commit }, resourceID) {
    if (resourceID !== '') {
      return new Promise((resolve, reject) => {
        users.checkWebinarRegistration(resourceID).then((response) => {
          const isRegistered = response.data.result.isRegistered
          commit(types.HAS_REGISTERED, isRegistered)
          resolve(isRegistered)
        }, (error) => {
          reject(error)
        })
      })
    } else {
      commit(types.HAS_REGISTERED, false)
    }
  },
  isSignupComplete ({ commit }, isComplete) {
    commit(types.IS_SIGNUP_COMPLETE, isComplete)
  },
  isUserApprover ({ commit }, { userID }) {
    if (userID) {
      return new Promise((resolve, reject) => {
        users.isUserApprover(userID).then((response) => {
          const isUserApprover = response.data.result.isApprover
          resolve({ isUserApprover })
        }, (error) => {
          reject(error)
        })
      })
    }
  },
  getSocialHandle ({ commit }, userid) {
    return new Promise((resolve, reject) => {
      users.getSocialHandle(userid).then((response) => {
        resolve(response.data)
        commit(types.SET_HANDLES, response.data.result.handles)
      }, (error) => {
        reject(error)
      })
    })
  },
  setSignupNames ({ commit }, data) {
    return new Promise((resolve, reject) => {
      commit(types.SET_SIGNUP_NAMES, data)
    })
  },
  saveSocialHandle ({ commit }, data) {
    return new Promise((resolve, reject) => {
      users.saveSocialHandle(data).then((response) => {
        resolve(response.data)
        commit(types.SAVE_HANDLE, response.data.result.userSocial)
      }, (error) => {
        reject(error)
      })
    })
  },
  deleteSocialHandle ({ commit }, data) {
    return new Promise((resolve, reject) => {
      users.deleteSocialHandle(data).then((response) => {
        resolve(response.data)
        commit(types.DELETE_HANDLE, response.data.result.userSocial)
      }, (error) => {
        reject(error)
      })
    })
  },
  signupByMicrosoft ({ commit, dispatch }, data) {
    return new Promise((resolve, reject) => {
      users.signupByMicrosoft(data).then((response) => {
        const user = response.data.result.user
        commit(types.SET_CURRENT_USER, user)
        commit(types.SET_USER_VERIFIED, false)
        commit(types.SET_HAS_PASSWORD, true)
        resolve(user)

        auth.storeTokens({ accessToken: response.headers.authorization, limited: true })
        auth.clearUTMSource()
        auth.clearReferralCode()

        resolve({ user: user, tokens: responseTokens(response) })
        dispatch('saveLocation', user.userName)

        dispatch('trackUserSignup')
      }, (error) => {
        reject(error)
      })
    })
  },
  teacherWelcome ({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      users.teacherWelcome(state.user.userName).then((response) => {
        resolve(responseTokens(response))
      }, (error) => {
        reject(error)
      })
    })
  },
  getGroupByCode ({ commit, state }, studCode) {
    return new Promise((resolve, reject) => {
      users.getGroupByCode(studCode).then((response) => {
        const group = response.data.result.group
        commit(types.SET_TEACHER_GROUP, group)
        resolve(response.data.result.group)
      }, (error) => {
        reject(error)
      })
    })
  },
  createGroupUser ({ commit, state }, data) {
    return new Promise((resolve, reject) => {
      users.createGroupUser(data).then((response) => {
        resolve(response)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateStudentStatus ({ commit, state }, { studentStatus, studentUsername }) {
    return new Promise((resolve, reject) => {
      users.updateStudentStatus(studentStatus, studentUsername).then((response) => {
        resolve(response.data.result.studentStatus)
      }, (error) => {
        reject(error)
      })
    })
  },
  createCode ({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      users.createCode().then((response) => {
        commit(types.TEACHER_CODE, response.data.result.teacherCode)
        resolve(response.data.result.teacherCode)
      }, (error) => {
        reject(error)
      })
    })
  },
  createNewGroup ({ commit, state }, data) {
    return new Promise((resolve, reject) => {
      users.createNewGroup(data).then((response) => {
        const group = response.data.result.group
        commit(types.SET_TEACHER_CLASSES, group)
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  updateClassName ({ commit, state }, params) {
    return new Promise((resolve, reject) => {
      users.updateClassName(params).then((response) => {
        commit(types.UPDATE_TEACHER_CLASS, response.data.result.group)
        resolve(response.data.result.group)
      }, (error) => {
        reject(error)
      })
    })
  },
  createUserName ({ commit, state }) {
    return new Promise((resolve, reject) => {
      users.createUserName().then((response) => {
        resolve(response.data.result.userName)
      }, (error) => {
        reject(error)
      })
    })
  },
  getClassesForTeacher ({ commit, state }, teacherId) {
    commit(types.CLEAR_TEACHER_CLASSES)
    return new Promise((resolve, reject) => {
      users.getClassesForTeacher(teacherId).then((response) => {
        const groupList = response.data.result.groupList
        groupList.forEach((group) => {
          commit(types.SET_TEACHER_CLASSES, group)
        })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  getStudentsByClass ({ commit, state }, params) {
    commit(types.CLEAR_STUDENTLIST)
    return new Promise((resolve, reject) => {
      users.getStudentsByClass(params).then((response) => {
        const studentList = response.data.result.studentList
        studentList.forEach((student) => {
          commit(types.SET_STUDENT_LIST, student)
        })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  updateStudentData ({ commit, state }, params) {
    return new Promise((resolve, reject) => {
      users.updateStudentData(params).then((response) => {
        const student = response.data.result.student
        commit(types.UPDATE_STUDENT, student)
        resolve(student)
      }, (error) => {
        reject(error)
      })
    })
  },
  deleteStudent ({ commit, state }, studentId) {
    return new Promise((resolve, reject) => {
      users.deleteStudent(studentId).then((response) => {
        const success = response.data.result.success
        commit(types.DELETE_STUDENT, studentId)
        resolve(success)
      }, (error) => {
        reject(error)
      })
    })
  },
  addStudentByTeacher ({ commit, state }, params) {
    return new Promise((resolve, reject) => {
      users.addStudentByTeacher(params).then((response) => {
        const student = response.data.result.user
        commit(types.SET_STUDENT_LIST, student)
        resolve(student)
      }, (error) => {
        reject(error)
      })
    })
  },
  createNewClass ({ commit, state }, teacherId) {
    return new Promise((resolve, reject) => {
      users.createNewClass(teacherId).then((response) => {
        const group = response.data.result.group
        commit(types.SET_TEACHER_GROUP, group)
        commit(types.SET_TEACHER_CLASSES, group)
        resolve(group)
      }, (error) => {
        reject(error)
      })
    })
  },
  getStudentQuestionId ({ commit, state }) {
    return new Promise((resolve, reject) => {
      users.getStudentQuestion().then((response) => {
        const question = response.data.result.question
        commit(types.SET_STUDENT_QUESTION_ID, question.id)
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  storeStudentName ({ commit, state }, params) {
    return new Promise((resolve, reject) => {
      users.storeStudentName(params).then((response) => {
        const answer = response.data.result.answeredQuestionName
        commit(types.SET_STUDENT_NAME, answer)
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  getStudentNameAnswer ({ commit, state }, { userId, questionId }) {
    return new Promise((resolve, reject) => {
      users.getStudentNameAnswer(userId, questionId).then((response) => {
        const answer = response.data.result.answer
        resolve(answer)
      }, (error) => {
        reject(error)
      })
    })
  }
}

const mutations = {
  [types.SET_CURRENT_USER] (state, user) {
    state.user = user
    state.limitedUser = null

    // gtm user-id tracking
    if (window.dataLayer) {
      window.dataLayer.push({ UserUUID: user ? user.uuid : null })
    }
  },
  [types.SET_TEACHER_GROUP] (state, group) {
    state.teacherGroup = group
  },
  [types.SET_TEACHER_CLASSES] (state, teacherClass) {
    state.teacherClasses.push(teacherClass)
  },
  [types.CLEAR_TEACHER_CLASSES] (state) {
    state.teacherClasses = []
  },
  [types.UPDATE_TEACHER_CLASS] (state, updatedClass) {
    state.teacherClasses.forEach((teacherClass, index) => {
      if (updatedClass.id === teacherClass.id) {
        state.teacherClasses[index].name = updatedClass.name
      }
    })
  },
  [types.SET_STUDENT_LIST] (state, student) {
    state.studentList.push(student)
  },
  [types.UPDATE_STUDENT] (state, updatedStudent) {
    state.studentList.forEach((student, index) => {
      if (updatedStudent.id === student.id) {
        state.studentList[index].userName = updatedStudent.userName
        state.studentList[index].firstName = updatedStudent.firstName
      }
    })
  },
  [types.CLEAR_STUDENTLIST] (state) {
    state.studentList = []
  },
  [types.DELETE_STUDENT] (state, studentId) {
    state.studentList[studentId] = null
  },
  [types.TEACHER_CODE] (state, classCode) {
    state.classCode = classCode
  },
  [types.SET_STUDENT_QUESTION_ID] (state, id) {
    state.studentQuestionId = id
  },
  [types.SET_STUDENT_NAME] (state, answer) {
    state.studentList.forEach((student, index) => {
      if (student.id === answer.userID) {
        state.studentList[index].userAnswer = answer
      }
    })
  },
  [types.SET_USER_CATEGORIES] (state, userCategories) {
    state.userSelectedCategories = userCategories
  },
  [types.SET_USER_FILTER_TAGS] (state, userFilterTags) {
    state.userFilterTags = userFilterTags
  },
  [types.SET_USER_FILTER_TAGS_REMEMBER] (state, remember) {
    state.userFilterTagsRemember = remember
  },
  [types.SET_LIMITED_USER] (state, user) {
    state.limitedUser = user
  },
  [types.SET_USER_VERIFIED] (state, verified) {
    state.verified = verified
  },
  [types.ADD_PROFILE] (state, params) {
    Vue.set(state.profiles, params.username, params.profile)
  },
  [types.MOVE_PROFILE] (state, params) {
    Vue.set(state.profiles, params.to, state.profiles[params.from])
    delete state.profiles[params.from]
  },
  [types.SET_HAS_PASSWORD] (state, hasPassword) {
    state.hasPassword = hasPassword
  },
  [types.SET_HAS_PHONE] (state, hasPhone) {
    state.hasPhone = hasPhone
  },
  [types.SET_SHOW_SIGNUP_SURVEY] (state, show) {
    state.showSignupSurvey = show
  },
  [types.SET_SHOW_SIGNUP_CATEGORIES] (state, show) {
    state.showSignupCategories = show
  },
  [types.SET_USER_SETTINGS] (state, settings) {
    state.settings = settings
  },
  [types.SET_USER_SETTINGS_PROMISE] (state, promise) {
    state.settingsPromise = promise
  },
  [types.SET_SKILLS] (state, params) {
    state.profiles[params.username].skills = params.skills
  },
  [types.SET_INTERESTS] (state, params) {
    state.profiles[params.username].interests = params.interests
  },
  [types.SET_HAVE_TOKEN] (state, have) {
    state.haveToken = have
  },
  [types.SET_FIREBASE_TOKEN] (state, token) {
    state.firebaseToken = token
  },
  [types.SET_CLOUD_MESSAGING_TOKEN] (state, token) {
    state.cloudMessagingToken = token
  },
  [types.ADD_PROFILE_COUNT] (state, params) {
    if (state.profiles[params.username]) {
      if (!state.profiles[params.username].counts) {
        Vue.set(state.profiles[params.username], 'counts', {})
      }
      Vue.set(state.profiles[params.username].counts, params.key, params.total)
    }
  },
  [types.SET_REFERRAL_CODE] (state, code) {
    state.referralCode = code
  },
  [types.SET_USER_AWARDS] (state, awards) {
    state.awards = awards
  },
  [types.HAS_YSC_INFO] (state, hasInfo) {
    state.hasYSCInfo = hasInfo
  },
  [types.HAS_AJAS_ROLE] (state, hasInfo) {
    state.hasAJASRole = hasInfo
  },
  [types.HAS_PARTNER_INFO] (state, hasInfo) {
    state.hasPartnerInfo = hasInfo
  },
  [types.SET_HANDLES] (state, handles) {
    state.handles = handles
  },
  [types.DELETE_HANDLE] (state, handles) {
    state.handles = state.handles.filter(object => {
      return object.handle !== handles.handle && object.platform !== handles.platform
    })
  },
  [types.SAVE_HANDLE] (state, handles) {
    state.handles.push(handles)
  },
  [types.HAS_CLICKED_RESOURCE_BUTTON] (state, isClicked) {
    state.resourceButtonClicked = isClicked
  },
  [types.HAS_REGISTERED] (state, registered) {
    state.isRegisteredForWebinar = registered
  },
  [types.IS_SIGNUP_COMPLETE] (state, isComplete) {
    state.isSignupComplete = isComplete
  },
  [types.SET_SIGNUP_NAMES] (state, names) {
    state.signupNames = names
  },
  [types.RESET_ON_LOGOUT] (state) {
    state.user = null
    state.verified = false
    state.haveToken = false
    state.loggedIn = false
    state.hasPassword = false
    state.hasPhone = false
    state.promptToSetPassword = false
    state.showSignupSurvey = false
    state.showSignupCategories = false
    state.profiles = {}
    state.skills = {}
    state.settings = {}
    state.settingsPromise = null
    state.firebaseToken = null
    state.cloudMessagingToken = null
    state.limitedUser = null
    state.profileUpdateCount = 0
    state.referralCode = null
    state.userSelectedCategories = []
    state.hasYSCInfo = false
    state.hasAJASRole = false
    state.hasPartnerInfo = false
    state.signupNames = {}
    state.group = {}
    state.teacherClasses = []
  },
  [types.UPDATE_USER_TYPE_ERROR] (state, error) {
    if (error.messages) {
      state.updateUserTypeError = error.messages[0]
    } else {
      state.updateUserTypeError = error
    }
  },
  [types.UPDATE_USER_TYPE_SUCCESS] (state) {
    state.updateUserTypeError = null
  }
}

const sharedMutations = [
  types.SET_CURRENT_USER,
  types.SET_LIMITED_USER,
  types.SET_USER_VERIFIED,
  types.SET_HAVE_TOKEN,
  types.SET_FIREBASE_TOKEN,
  types.SET_CLOUD_MESSAGING_TOKEN,
  types.RESET_ON_LOGOUT,
  types.SET_TEACHER_GROUP
]

export default {
  state,
  getters,
  actions,
  mutations,
  sharedMutations
}
